import type { Address } from "viem"
import type { Config } from "wagmi"
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx"
import { parseUnits } from "viem"
import { readContract } from "@wagmi/core"

import type { EmitEvent } from "@/domains/core"
import type { IEventBus } from "@/domains/events"
import type { ManualClOrchestrator } from "@/domains/thick-v1/manual-cl-orchestrator"
import type { ManualClPoolRepository } from "@/domains/thick-v1/manual-cl-repository"
import type { ManualClPoolStore } from "@/domains/thick-v1/manual-cl-store"
import type { PriceRangeCalculationResult } from "@/domains/thick-v1/stores/price-range-calculation/interfaces"
import type { PriceStrategyConfig } from "@/domains/thick-v1/stores/price-strategies"
import type { ThickV1TickDistributionStore } from "@/domains/thick-v1/tick-distribution"
import type { IApprovalModeProvider } from "@/domains/settings/interfaces"
import type { IWalletConnectionStore } from "@/domains/wallet/types"
import { erc20Abi } from "@/domains/abis/erc20-abi"
import { DEFAULT_SLIPPAGE } from "@/domains/common/config/slippage"
import { approveOwnerSpenderAllowance, waitForAllowance } from "@/domains/on-chain"
import { createPositionInteraction, MANUAL_CL_RECEIPT_WAIT_OPTIONS } from "@/domains/thick-v1/interactions"
import { EventEmitter } from "@/domains/library"
import {
  getFeeToTicksMapForChain,
  MAX_TICK,
  MIN_TICK,
  NFP_MANAGER_ADDRESS,
} from "@/domains/thick-v1/constants"
import { ManualClPositionCreatedEvent } from "@/domains/thick-v1/events"
import { humanizeTransactionError } from "@/domains/thick-v1/utils/humanize-transaction-error"
import {
  quoteToken0FromToken1,
  quoteToken1FromToken0,
} from "@/domains/thick-v1/stores/deposit-counterpart-quote"
import { DEFAULT_PRICE_RANGE_RESULT } from "@/domains/thick-v1/stores/price-range-calculation/interfaces"
import { PriceRangeCalculationStrategyFactory } from "@/domains/thick-v1/stores/price-range-calculation/price-range-calculation-strategy-factory"
import {
  calculateStrategyTicks,
  getPriceStrategyConfigs,
  PriceStrategy,
} from "@/domains/thick-v1/stores/price-strategies"
import {
  calculatePriceDifference,
  roundToTickSpacing,
  tickToPriceString,
  tryParseTickFromPrice,
} from "@/domains/thick-v1/utils/price-to-tick-conversion"
import {
  concentratedApr,
  getSqrtRatioAtTick,
  positionFromAmount0,
  positionFromAmount1,
} from "@/domains/manual-cl-core"
import { WalletDisconnectedEvent } from "@/domains/wallet/events"

export type DepositMode = "single_token0" | "single_token1" | "dual" | "unknown"

/**
 * Stable names emitted for every step of the create-position workflow. Must
 * stay in sync with `CreatePositionStepName` in the interaction file — the
 * two exist so the producer (interaction) and the consumer (store) share a
 * common string vocabulary without importing each other.
 */
export type CreatePositionStepName = "approve:token0" | "approve:token1" | "mint"

export interface CreatePositionStep {
  id: CreatePositionStepName
  status: "active" | "done" | "failed"
}

/**
 * Enumerates the state the CTA button is in. The view layer consumes this
 * rather than a grab-bag of booleans so the label and click handler stay in
 * sync. `approve-token0` and `approve-token1` are reachable only when the
 * pre-check has seen a concrete shortfall; otherwise the user gets the
 * full-chain `create-position` path (approvals + mint) in a single click.
 */
export type CreatePositionCtaState =
  | "connect"
  | "enter-amount"
  | "loading-approvals"
  | "approve-token0"
  | "approve-token1"
  | "create-position"
  | "in-flight"

const DEFAULT_DEADLINE_SECONDS = 20 * 60
const MIN_DEADLINE_SECONDS = 60
const PRICE_RANGE_TICK_STORAGE_DECIMALS = 18

/**
 * Thick-v1 create-position store.
 *
 * Moved from src/domains/manual-cl/stores/ as part of unit-006. Behavior is
 * preserved one-to-one; the notable additions are:
 *   • `setMinTick` / `setMaxTick` drop-in tick setters for the upcoming
 *     range-brush composite (drags emit ticks, not prices).
 *   • `increment*` / `decrement*` are now expressed in terms of those tick
 *     setters so every mutation path funnels through a single primitive.
 *   • Dependent-amount calls now pass `sqrtPriceX96Current` — preferring the
 *     slot0 value on the pool store when available for bit-exact math, and
 *     falling back to `getSqrtRatioAtTick(currentTick)` otherwise.
 */
export class CreatePositionStore {
  // Pool selection
  pool: ManualClPoolStore | undefined = undefined

  // Price range (price-based, not tick-based)
  minPrice: string | undefined = undefined
  maxPrice: string | undefined = undefined
  rangeSpreadPctInput: string = ""
  inputMode: "price" | "percentage" = "price"
  fullRange: boolean = false
  isPriceRangeInverted: boolean = false

  // Selected price strategy
  selectedStrategy: PriceStrategy | undefined = undefined

  // Deposit amounts
  amount0: string = ""
  amount1: string = ""
  activeDepositInput: "token0" | "token1" | undefined = undefined

  // Transaction settings
  slippage: number = DEFAULT_SLIPPAGE
  deadline: number = 0 // Display timestamp; submit uses deadlineDurationSeconds.
  deadlineDurationSeconds: number = DEFAULT_DEADLINE_SECONDS

  // Creation state
  isCreating: boolean = false
  creationError: string | undefined = undefined

  // Loading state
  isLoadingPool: boolean = false

  /**
   * Current ERC20 allowance of the connected account for NFPM, cached so the
   * CTA can decide between "Approve" and "Create position" before the user
   * clicks. `undefined` means the allowance has not been fetched yet (either
   * no pool is loaded or no wallet is connected); `0n` means the pool is
   * loaded but the user has not approved NFPM yet. Kept separate so the view
   * can distinguish "loading" from "definitely needs approval".
   */
  token0AllowanceWei: bigint | undefined = undefined
  token1AllowanceWei: bigint | undefined = undefined
  isLoadingAllowances: boolean = false

  /**
   * Stepper state driven by the typed emitter events fired by
   * `createPositionInteraction`. `activeStep` is the step that is currently
   * running (approve token0, approve token1, or mint) and `completedSteps`
   * is every step that has already landed successfully. The composite maps
   * these into the `CreatePositionProcessView` step list.
   */
  activeStep: CreatePositionStep | undefined = undefined
  completedSteps: Array<CreatePositionStepName> = []
  stepErrorMessage: string | undefined = undefined
  isCheckingProcess: boolean = false
  activeStepStartedAt: number | undefined = undefined
  pendingStepId: CreatePositionStepName | undefined = undefined
  pendingTxHash: string | undefined = undefined
  createdTokenId: bigint | undefined = undefined
  createdPoolAddress: Address | undefined = undefined

  /**
   * Listener registered on the emitter so we can mirror the interaction's
   * typed step events into the `activeStep` / `completedSteps` observables
   * above. Kept as a field so `dispose()` can detach it.
   */
  private stepEmitterListener: ((name: string, data: EmitEvent) => void) | undefined
  private positionCreatedHandler: ((event: ManualClPositionCreatedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined
  private strategyTickReactionDispose: (() => void) | undefined
  private stepClearTimer: ReturnType<typeof setTimeout> | undefined

  constructor(
    private readonly orchestrator: ManualClOrchestrator,
    private readonly poolRepository: ManualClPoolRepository,
    private readonly chainId: number,
    private readonly config: Config,
    private readonly eventBus: IEventBus,
    private readonly emitter: EventEmitter<EmitEvent>,
    private readonly walletStore: IWalletConnectionStore,
    private readonly approvalModeProvider: IApprovalModeProvider,
    /**
     * Optional: the shared tick-distribution store. Needed for
     * `estimatedApr`, which has to know the in-range virtual liquidity
     * sitting against the prospective new position. Left optional so unit
     * tests can run the store without paying for a full tick book.
     */
    private readonly tickDistributionStore?: ThickV1TickDistributionStore,
  ) {
    makeObservable(this, {
      pool: observable,
      minPrice: observable,
      maxPrice: observable,
      rangeSpreadPctInput: observable,
      inputMode: observable,
      fullRange: observable,
      isPriceRangeInverted: observable,
      selectedStrategy: observable,
      amount0: observable,
      amount1: observable,
      activeDepositInput: observable,
      slippage: observable,
      deadline: observable,
      deadlineDurationSeconds: observable,
      isCreating: observable,
      creationError: observable,
      isLoadingPool: observable,
      token0AllowanceWei: observable,
      token1AllowanceWei: observable,
      isLoadingAllowances: observable,
      activeStep: observable,
      completedSteps: observable,
      stepErrorMessage: observable,
      isCheckingProcess: observable,
      activeStepStartedAt: observable,
      pendingStepId: observable,
      pendingTxHash: observable,
      createdTokenId: observable,
      createdPoolAddress: observable,

      // Actions
      setPool: action,
      setMinPrice: action,
      setMaxPrice: action,
      setRangeSpreadPctInput: action,
      setMinTick: action,
      setMaxTick: action,
      setInputMode: action,
      toggleInputMode: action,
      setPriceRangeInverted: action,
      togglePriceRangeInversion: action,
      setFullRange: action,
      setSelectedStrategy: action,
      incrementMinPrice: action,
      decrementMinPrice: action,
      incrementMaxPrice: action,
      decrementMaxPrice: action,
      applyPriceStrategy: action,
      setAmount0: action,
      setAmount1: action,
      setActiveDepositInput: action,
      setAmount0FromUserInput: action,
      setAmount1FromUserInput: action,
      setSingleSidedToken0FromUserInput: action,
      setSingleSidedToken1FromUserInput: action,
      setDualFromToken0UserInput: action,
      setDualFromToken1UserInput: action,
      reconcileDepositMode: action,
      fillMaxAmount0: action,
      fillMaxAmount1: action,
      setSlippage: action,
      setDeadline: action,
      loadPoolByAddress: action,
      createPosition: action,
      createPositionForConnectedWallet: action,
      refreshAllowances: action,
      startNextStepForConnectedWallet: action,
      syncProcessFromChain: action,
      clearStepProgress: action,
      reset: action,

      // Computed properties
      hasPool: computed,
      priceRangeCalculation: computed,
      depositMode: computed,
      isToken0DepositDisabled: computed,
      isToken1DepositDisabled: computed,
      hasValidAmounts: computed,
      canCreatePosition: computed,
      tickSpacing: computed,
      currentTick: computed,
      currentSqrtPriceX96: computed,
      currentPrice: computed,
      displayedCurrentPrice: computed,
      displayedPricesAtTicks: computed,
      displayedPriceDifferences: computed,
      amount0Min: computed,
      amount1Min: computed,
      priceStrategyConfigs: computed,
      isRangeInvalid: computed,
      isWalletConnected: computed,
      isProposedRangeInRange: computed,
      liquidityCoveragePct: computed,
      amount0UsdValue: computed,
      amount1UsdValue: computed,
      totalDepositUsdValue: computed,
      estimatedApr: computed,
      amount0DesiredWei: computed,
      amount1DesiredWei: computed,
      requiredApprovals: computed,
      approvalStatus: computed,
      ctaState: computed,
      processSteps: computed,
    })

    // Set default deadline duration (20 minutes from submit time)
    this.setDeadline(nowSeconds() + DEFAULT_DEADLINE_SECONDS)

    // Clear user-entered state on wallet disconnect (matches other domains)
    this.walletDisconnectedHandler = this.handleWalletDisconnected.bind(this)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)

    // Refresh allowances whenever a position is successfully created so the
    // next create-position session starts from a fresh allowance snapshot
    // (the mint drained the prior allowance when using strict mode).
    this.positionCreatedHandler = (event) => {
      runInAction(() => {
        this.createdTokenId = event.payload.tokenId
        this.createdPoolAddress = event.payload.poolAddress
        this.pendingTxHash = event.payload.transactionHash
        this.pendingStepId = "mint"
      })
      void this.refreshAllowances()
    }
    this.eventBus.subscribe(ManualClPositionCreatedEvent.TYPE, this.positionCreatedHandler)

    // Translate the interaction's typed step events into observable stepper
    // state. The toast handler keeps listening to the same events via `*`;
    // we're just mirroring them into the card UI so the user sees progress
    // in-place without hunting the toast tray.
    this.stepEmitterListener = (name, data) => this.handleStepEvent(name, data)
    this.emitter.on("*", this.stepEmitterListener)

    // Keep preset strategies (stable / wide / one-sided) centered on the live
    // pool tick. Without this, `minPrice` and `maxPrice` are frozen at the
    // moment the strategy was applied; once the pool tick drifts the chart
    // shows an asymmetric range (e.g. "MIN 2 ticks / MAX 4 ticks") while the
    // dependent-amount math silently uses a fresh symmetric one — so the UI
    // and the submitted mint tx end up out of sync.
    this.strategyTickReactionDispose = reaction(
      () => ({
        currentTick: this.currentTick,
        tickSpacing: this.tickSpacing,
        strategy: this.selectedStrategy,
        token0: this.pool?.token0,
        token1: this.pool?.token1,
      }),
      ({ currentTick, tickSpacing, strategy, token0, token1 }) => {
        if (
          strategy === undefined ||
          strategy === PriceStrategy.FULL_RANGE ||
          currentTick === undefined ||
          !tickSpacing ||
          !token0 ||
          !token1
        ) {
          return
        }
        const { lowerTick, upperTick } = calculateStrategyTicks({
          strategy,
          currentTick,
          tickSpacing,
        })
        const nextMinPrice = tickToPriceString(lowerTick, token0, token1, PRICE_RANGE_TICK_STORAGE_DECIMALS)
        const nextMaxPrice = tickToPriceString(upperTick, token0, token1, PRICE_RANGE_TICK_STORAGE_DECIMALS)
        // Write directly — `setMinPrice` / `setMaxPrice` would clear the
        // selected strategy as a side effect (they treat any price change as
        // a manual override), which would make this reaction fire only once.
        runInAction(() => {
          if (this.minPrice !== nextMinPrice) this.minPrice = nextMinPrice
          if (this.maxPrice !== nextMaxPrice) this.maxPrice = nextMaxPrice
          if (this.fullRange) this.fullRange = false
        })
      },
      { fireImmediately: false },
    )
  }

  /**
   * Release subscriptions. Call when the store instance is being discarded
   * (e.g. when leaving the create-position page or disposing the namespace).
   */
  dispose(): void {
    this.strategyTickReactionDispose?.()
    this.strategyTickReactionDispose = undefined
    if (this.walletDisconnectedHandler) {
      this.eventBus.unsubscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)
      this.walletDisconnectedHandler = undefined
    }
    if (this.positionCreatedHandler) {
      this.eventBus.unsubscribe(ManualClPositionCreatedEvent.TYPE, this.positionCreatedHandler)
      this.positionCreatedHandler = undefined
    }
    if (this.stepEmitterListener) {
      this.emitter.off("*", this.stepEmitterListener)
      this.stepEmitterListener = undefined
    }
    if (this.stepClearTimer) {
      clearTimeout(this.stepClearTimer)
      this.stepClearTimer = undefined
    }
  }

  private handleWalletDisconnected(_event: WalletDisconnectedEvent): void {
    this.amount0 = ""
    this.amount1 = ""
    this.creationError = undefined
    this.token0AllowanceWei = undefined
    this.token1AllowanceWei = undefined
    this.clearStepProgress()
  }

  /**
   * Bridge from `emitter` events to stepper observables. The interaction
   * fires one `info` event when a step starts, then either a `success`
   * event (which we treat as "done") or an `error` event (which we treat
   * as "failed"). Events that don't correspond to a known step id are
   * ignored so third-party subscribers on the same emitter can't corrupt
   * our state.
   */
  private handleStepEvent(name: string, data: EmitEvent): void {
    if (name !== "approve:token0" && name !== "approve:token1" && name !== "mint") {
      return
    }
    const stepId = name as CreatePositionStepName

    runInAction(() => {
      if (data.variant === "info" || data.variant === "waiting") {
        this.activeStep = { id: stepId, status: "active" }
        this.activeStepStartedAt = Date.now()
        this.pendingStepId = stepId
        this.stepErrorMessage = undefined
        return
      }

      if (data.variant === "success") {
        if (!this.completedSteps.includes(stepId)) {
          this.completedSteps.push(stepId)
        }
        if (this.activeStep?.id === stepId) {
          this.activeStep = undefined
        }
        this.activeStepStartedAt = undefined
        if (this.pendingStepId === stepId) {
          this.pendingStepId = undefined
        }
        return
      }

      if (data.variant === "error") {
        this.activeStep = { id: stepId, status: "failed" }
        this.activeStepStartedAt = undefined
        this.stepErrorMessage =
          data.description ?? humanizeTransactionError(data.errorMessage) ?? "Transaction failed."
        return
      }
    })
  }

  // Actions
  setPool(pool: ManualClPoolStore | undefined): void {
    this.pool = pool
    if (pool) {
      // Reset price range when pool changes
      this.minPrice = undefined
      this.maxPrice = undefined
      this.rangeSpreadPctInput = ""
      this.selectedStrategy = undefined
    }
    this.reconcileDepositMode()
  }

  setMinPrice(price: string | undefined): void {
    // Only update if value actually changed to prevent unnecessary re-computations
    if (this.minPrice === price) {
      return
    }

    this.minPrice = price
    this.fullRange = false
    // Clear strategy when user manually sets price
    if (price !== undefined) {
      this.selectedStrategy = undefined
    }
    this.reconcileDepositMode()
  }

  setMaxPrice(price: string | undefined): void {
    // Only update if value actually changed to prevent unnecessary re-computations
    if (this.maxPrice === price) {
      return
    }
    this.maxPrice = price
    this.fullRange = false
    // Clear strategy when user manually sets price
    if (price !== undefined) {
      this.selectedStrategy = undefined
    }
    this.reconcileDepositMode()
  }

  /**
   * Set a symmetric spread around the current pool price. A user-entered
   * value of `5` means "lower bound 5% below spot, upper bound 5% above
   * spot" — not 5% total width. The method owns parsing and tick conversion
   * so views can stay dumb and simply pass the input string through.
   */
  setRangeSpreadPctInput(value: string): void {
    this.rangeSpreadPctInput = value
    const spreadPct = Number(value)
    if (
      !this.pool ||
      !this.tickSpacing ||
      this.currentTick === undefined ||
      this.currentPrice === undefined ||
      !Number.isFinite(spreadPct) ||
      spreadPct <= 0 ||
      spreadPct >= 100
    ) {
      return
    }

    const lowerPrice = this.currentPrice * (1 - spreadPct / 100)
    const upperPrice = this.currentPrice * (1 + spreadPct / 100)
    const lowerTick = tryParseTickFromPrice(
      String(lowerPrice),
      this.pool.token0,
      this.pool.token1,
      this.tickSpacing,
    )
    const upperTick = tryParseTickFromPrice(
      String(upperPrice),
      this.pool.token0,
      this.pool.token1,
      this.tickSpacing,
    )
    if (lowerTick === undefined || upperTick === undefined) return

    let tickLower = Math.min(lowerTick, upperTick)
    let tickUpper = Math.max(lowerTick, upperTick)
    if (tickUpper - tickLower < this.tickSpacing) {
      const centered = roundToTickSpacing(this.currentTick, this.tickSpacing)
      tickLower = Math.max(MIN_TICK, centered - this.tickSpacing)
      tickUpper = Math.min(MAX_TICK, centered + this.tickSpacing)
    }
    if (tickUpper <= tickLower) return

    this.fullRange = false
    this.selectedStrategy = undefined
    this.minPrice = tickToPriceString(
      tickLower,
      this.pool.token0,
      this.pool.token1,
      PRICE_RANGE_TICK_STORAGE_DECIMALS,
    )
    this.maxPrice = tickToPriceString(
      tickUpper,
      this.pool.token0,
      this.pool.token1,
      PRICE_RANGE_TICK_STORAGE_DECIMALS,
    )
    this.reconcileDepositMode()
  }

  /**
   * Tick-first primitive used by the range-brush composite and by the
   * increment/decrement helpers. Snaps to `tickSpacing`, clamps to MIN_TICK,
   * and writes the resulting price string through `setMinPrice` so strategy
   * clearing + deposit-mode reconciliation still run.
   *
   * Also enforces `minTick + tickSpacing <= maxTick`: Uniswap V3 requires a
   * strictly ordered range, and without this the "+" button on the min card
   * (or dragging the min brush past the max brush) would let the range
   * collapse to a single tick ("MIN 0 ticks") or invert (min > max). We
   * intentionally do NOT clamp against `currentTick` here — single-sided
   * out-of-range positions are a first-class feature (see the
   * `ONE_SIDED_LOWER` / `ONE_SIDED_UPPER` strategies), and both bounds can
   * legitimately sit on the same side of the current tick.
   *
   * Clamping here is the single chokepoint for all user-driven tick edits
   * (brush drag + "+/-" buttons both route through this method), so preset
   * strategies — which always produce valid ordered ranges — are unaffected.
   */
  setMinTick(tick: number): void {
    if (!this.pool || !this.tickSpacing) return
    const snapped = roundToTickSpacing(tick, this.tickSpacing)
    if (snapped < MIN_TICK) return
    const currentMaxTick = this.priceRangeCalculation.ticks[1]
    let clamped = snapped
    if (currentMaxTick !== undefined) {
      const maxAllowed = currentMaxTick - this.tickSpacing
      if (clamped > maxAllowed) {
        if (maxAllowed < MIN_TICK) return
        clamped = maxAllowed
      }
    }
    const priceString = tickToPriceString(
      clamped,
      this.pool.token0,
      this.pool.token1,
      PRICE_RANGE_TICK_STORAGE_DECIMALS,
    )
    this.setMinPrice(priceString)
  }

  /**
   * Tick-first primitive mirroring `setMinTick` for the upper bound. Enforces
   * `maxTick >= minTick + tickSpacing` so the range can never collapse or
   * invert via the "-" button or brush drag.
   */
  setMaxTick(tick: number): void {
    if (!this.pool || !this.tickSpacing) return
    const snapped = roundToTickSpacing(tick, this.tickSpacing)
    if (snapped > MAX_TICK) return
    const currentMinTick = this.priceRangeCalculation.ticks[0]
    let clamped = snapped
    if (currentMinTick !== undefined) {
      const minAllowed = currentMinTick + this.tickSpacing
      if (clamped < minAllowed) {
        if (minAllowed > MAX_TICK) return
        clamped = minAllowed
      }
    }
    const priceString = tickToPriceString(
      clamped,
      this.pool.token0,
      this.pool.token1,
      PRICE_RANGE_TICK_STORAGE_DECIMALS,
    )
    this.setMaxPrice(priceString)
  }

  setInputMode(mode: "price" | "percentage"): void {
    this.inputMode = mode
  }

  toggleInputMode(): void {
    this.inputMode = this.inputMode === "price" ? "percentage" : "price"
  }

  setPriceRangeInverted(value: boolean): void {
    this.isPriceRangeInverted = value
  }

  togglePriceRangeInversion(): void {
    this.isPriceRangeInverted = !this.isPriceRangeInverted
  }

  setFullRange(isFullRange: boolean): void {
    this.fullRange = isFullRange
    if (isFullRange) {
      this.minPrice = undefined
      this.maxPrice = undefined
      this.selectedStrategy = undefined
    }
  }

  setSelectedStrategy(strategy: PriceStrategy | undefined): void {
    this.selectedStrategy = strategy
  }

  setAmount0(amount: string): void {
    this.amount0 = amount
  }

  setAmount1(amount: string): void {
    this.amount1 = amount
  }

  setActiveDepositInput(input: "token0" | "token1" | undefined): void {
    this.activeDepositInput = input
  }

  setAmount0FromUserInput(input: string): void {
    const mode = this.depositMode
    if (mode === "single_token0") {
      this.setSingleSidedToken0FromUserInput(input)
      return
    }
    if (mode === "single_token1") {
      // Token0 is disabled in this mode; ignore.
      return
    }
    this.setDualFromToken0UserInput(input)
  }

  setAmount1FromUserInput(input: string): void {
    const mode = this.depositMode
    if (mode === "single_token1") {
      this.setSingleSidedToken1FromUserInput(input)
      return
    }
    if (mode === "single_token0") {
      // Token1 is disabled in this mode; ignore.
      return
    }
    this.setDualFromToken1UserInput(input)
  }

  setSingleSidedToken0FromUserInput(input: string): void {
    const pool = this.pool
    if (!pool) {
      return
    }
    this.activeDepositInput = "token0"
    this.amount0 = this.sanitizeAmountFromUserInput(input, pool.token0.decimals)
    if (this.amount1 !== "0") {
      this.amount1 = "0"
    }
  }

  setSingleSidedToken1FromUserInput(input: string): void {
    const pool = this.pool
    if (!pool) {
      return
    }
    this.activeDepositInput = "token1"
    this.amount1 = this.sanitizeAmountFromUserInput(input, pool.token1.decimals)
    if (this.amount0 !== "0") {
      this.amount0 = "0"
    }
  }

  setDualFromToken0UserInput(input: string): void {
    const pool = this.pool
    const sqrtPriceX96Current = this.currentSqrtPriceX96
    const [tickLower, tickUpper] = this.getActiveRangeTicksForAmountMath()
    if (!pool) {
      return
    }
    this.activeDepositInput = "token0"

    const sanitized = this.sanitizeAmountFromUserInput(input, pool.token0.decimals)
    this.amount0 = sanitized
    if (sanitized === "" || sanitized === "0" || sanitized === "0.") {
      this.amount1 = "0"
      return
    }

    const amount0 = parseUnits(sanitized, pool.token0.decimals)
    const amount1 = quoteToken1FromToken0(amount0, {
      sqrtPriceX96Current,
      tickLower,
      tickUpper,
      token0: pool.token0,
      token1: pool.token1,
      fee: pool.feeTier,
    })
    if (amount1 === undefined) {
      this.amount1 = ""
      return
    }
    try {
      this.amount1 = pool.token1.formatUnits(amount1)
    } catch (error) {
      console.error("[CreatePositionStore.setDualFromToken0UserInput] Dependent amount calc failed", error)
    }
  }

  setDualFromToken1UserInput(input: string): void {
    const pool = this.pool
    const sqrtPriceX96Current = this.currentSqrtPriceX96
    const [tickLower, tickUpper] = this.getActiveRangeTicksForAmountMath()
    if (!pool) {
      return
    }
    this.activeDepositInput = "token1"

    const sanitized = this.sanitizeAmountFromUserInput(input, pool.token1.decimals)
    this.amount1 = sanitized
    if (sanitized === "" || sanitized === "0" || sanitized === "0.") {
      this.amount0 = "0"
      return
    }

    const amount1 = parseUnits(sanitized, pool.token1.decimals)
    const amount0 = quoteToken0FromToken1(amount1, {
      sqrtPriceX96Current,
      tickLower,
      tickUpper,
      token0: pool.token0,
      token1: pool.token1,
      fee: pool.feeTier,
    })
    if (amount0 === undefined) {
      this.amount0 = ""
      return
    }
    try {
      this.amount0 = pool.token0.formatUnits(amount0)
    } catch (error) {
      console.error("[CreatePositionStore.setDualFromToken1UserInput] Dependent amount calc failed", error)
    }
  }

  reconcileDepositMode(): void {
    const mode = this.depositMode
    if (mode === "single_token0") {
      if (this.amount1 !== "0") {
        this.amount1 = "0"
      }
      if (this.activeDepositInput === undefined) {
        this.activeDepositInput = "token0"
      }
      return
    }
    if (mode === "single_token1") {
      if (this.amount0 !== "0") {
        this.amount0 = "0"
      }
      if (this.activeDepositInput === undefined) {
        this.activeDepositInput = "token1"
      }
      return
    }
    if (mode === "dual") {
      if (this.activeDepositInput === "token0") {
        this.setDualFromToken0UserInput(this.amount0)
      } else if (this.activeDepositInput === "token1") {
        this.setDualFromToken1UserInput(this.amount1)
      }
    }
  }

  fillMaxAmount0(): void {
    const token = this.pool?.token0
    if (!token) {
      return
    }
    const maxAmount = token.formatUnits(token.balance)
    this.setAmount0FromUserInput(maxAmount)
  }

  fillMaxAmount1(): void {
    const token = this.pool?.token1
    if (!token) {
      return
    }
    const maxAmount = token.formatUnits(token.balance)
    this.setAmount1FromUserInput(maxAmount)
  }

  /**
   * For strategy-driven ranges, derive ticks directly from the selected
   * strategy so dependent amount math is independent from display-price
   * rounding. Custom/manual ranges continue to use parsed ticks.
   */
  private getActiveRangeTicksForAmountMath(): [number | undefined, number | undefined] {
    if (
      this.selectedStrategy !== undefined &&
      this.selectedStrategy !== PriceStrategy.FULL_RANGE &&
      this.currentTick !== undefined &&
      this.tickSpacing !== undefined
    ) {
      const { lowerTick, upperTick } = calculateStrategyTicks({
        strategy: this.selectedStrategy,
        currentTick: this.currentTick,
        tickSpacing: this.tickSpacing,
      })
      return [lowerTick, upperTick]
    }

    const [tickLower, tickUpper] = this.priceRangeCalculation.ticks
    if (tickLower !== undefined && tickUpper !== undefined) {
      return [tickLower, tickUpper]
    }

    if (!this.pool || !this.tickSpacing || !this.minPrice || !this.maxPrice) {
      return [undefined, undefined]
    }

    const parsedLower = tryParseTickFromPrice(
      this.minPrice,
      this.pool.token0,
      this.pool.token1,
      this.tickSpacing,
    )
    const parsedUpper = tryParseTickFromPrice(
      this.maxPrice,
      this.pool.token0,
      this.pool.token1,
      this.tickSpacing,
    )
    return [parsedLower, parsedUpper]
  }

  private sanitizeAmountFromUserInput(input: string, decimals: number | undefined): string {
    // Allow clearing
    if (input === "") {
      return ""
    }

    // Only allow digits and a single dot
    if (!/^\d*\.?\d*$/.test(input)) {
      return ""
    }

    if (decimals === undefined) {
      return input
    }

    const parts = input.split(".")
    if (parts.length <= 1) {
      return input
    }

    const whole = parts[0] ?? ""
    const fraction = parts[1] ?? ""
    if (fraction.length <= decimals) {
      return input
    }

    return `${whole}.${fraction.slice(0, decimals)}`
  }

  setSlippage(slippage: number): void {
    this.slippage = slippage
  }

  setDeadline(deadline: number): void {
    this.deadline = deadline
    this.deadlineDurationSeconds = Math.max(
      MIN_DEADLINE_SECONDS,
      Math.floor(deadline - nowSeconds()),
    )
  }

  private getSubmitDeadline(): number {
    const deadline = nowSeconds() + this.deadlineDurationSeconds
    this.deadline = deadline
    return deadline
  }

  async loadPoolByAddress(address: Address): Promise<void> {
    this.isLoadingPool = true
    this.creationError = undefined
    try {
      const pool = await this.orchestrator.queryPoolByAddress(address, this.chainId)

      runInAction(() => {
        this.pool = pool
        this.isLoadingPool = false
        if (!pool) {
          this.creationError = "Pool not found"
        } else {
          // Initialize default price range if not already set. "Wide" (−50% to
          // +100%) is the default because it is the most common shape LPs
          // actually deploy and because the tighter presets (±3 ticks) leave
          // the range-brush viewport zoomed so far in that the drag handles
          // are effectively unusable on first load. Users who want a tighter
          // range pick Stable / One-sided from the preset row.
          //
          // We compute ticks directly (rather than calling `applyPriceStrategy`)
          // to avoid racing with the `priceRangeCalculation` computed — the
          // strategy setter reads tick state that hasn't finished settling
          // the first time `loadPool` runs.
          const currentTick = pool.currentTick
          const tickSpacing = this.tickSpacing
          if (
            this.minPrice === undefined &&
            this.maxPrice === undefined &&
            currentTick !== undefined &&
            tickSpacing
          ) {
            const { lowerTick, upperTick } = calculateStrategyTicks({
              strategy: PriceStrategy.WIDE,
              currentTick,
              tickSpacing,
            })
            const minPriceString = tickToPriceString(
              lowerTick,
              pool.token0,
              pool.token1,
              PRICE_RANGE_TICK_STORAGE_DECIMALS,
            )
            const maxPriceString = tickToPriceString(
              upperTick,
              pool.token0,
              pool.token1,
              PRICE_RANGE_TICK_STORAGE_DECIMALS,
            )
            this.minPrice = minPriceString
            this.maxPrice = maxPriceString
            this.selectedStrategy = PriceStrategy.WIDE
          }
        }
        this.reconcileDepositMode()
      })
      // Kick off a background allowance fetch so the CTA label (e.g.
      // "Approve WETH") can be correct before the user enters amounts.
      // Fire-and-forget: the allowance read is resilient to wallet /
      // network failures and never blocks pool load.
      void this.refreshAllowances()
    } catch (error) {
      runInAction(() => {
        this.isLoadingPool = false
        this.creationError = error instanceof Error ? error.message : "Failed to load pool"
      })
    }
  }

  incrementMinPrice(): void {
    if (!this.pool || !this.tickSpacing || this.currentTick === undefined) return
    const baseTick = this.minPrice
      ? (tryParseTickFromPrice(this.minPrice, this.pool.token0, this.pool.token1, this.tickSpacing) ??
          this.currentTick)
      : this.currentTick
    this.setMinTick(baseTick + (this.isPriceRangeInverted ? -this.tickSpacing : this.tickSpacing))
  }

  decrementMinPrice(): void {
    if (!this.pool || !this.tickSpacing || this.currentTick === undefined) return
    const baseTick = this.minPrice
      ? (tryParseTickFromPrice(this.minPrice, this.pool.token0, this.pool.token1, this.tickSpacing) ??
          this.currentTick)
      : this.currentTick
    this.setMinTick(baseTick + (this.isPriceRangeInverted ? this.tickSpacing : -this.tickSpacing))
  }

  incrementMaxPrice(): void {
    if (!this.pool || !this.tickSpacing || this.currentTick === undefined) return
    const baseTick = this.maxPrice
      ? (tryParseTickFromPrice(this.maxPrice, this.pool.token0, this.pool.token1, this.tickSpacing) ??
          this.priceRangeCalculation.ticks[1] ??
          this.currentTick)
      : (this.priceRangeCalculation.ticks[1] ?? this.currentTick)
    this.setMaxTick(baseTick + (this.isPriceRangeInverted ? -this.tickSpacing : this.tickSpacing))
  }

  decrementMaxPrice(): void {
    if (!this.pool || !this.tickSpacing || this.currentTick === undefined) return
    const baseTick = this.maxPrice
      ? (tryParseTickFromPrice(this.maxPrice, this.pool.token0, this.pool.token1, this.tickSpacing) ??
          this.priceRangeCalculation.ticks[1] ??
          this.currentTick)
      : (this.priceRangeCalculation.ticks[1] ?? this.currentTick)
    this.setMaxTick(baseTick + (this.isPriceRangeInverted ? this.tickSpacing : -this.tickSpacing))
  }

  applyPriceStrategy(strategy: PriceStrategy): void {
    if (!this.pool || !this.tickSpacing) {
      return
    }

    if (strategy === PriceStrategy.FULL_RANGE) {
      // Full range: drive display + ticks via FullRangeCalculationStrategy (0 — ∞)
      this.setFullRange(true)
      this.setSelectedStrategy(PriceStrategy.FULL_RANGE)
      this.reconcileDepositMode()
      return
    }

    if (this.currentTick === undefined) {
      return
    }

    // Tick-first: compute ticks (snapped), then derive prices.
    const { lowerTick, upperTick } = calculateStrategyTicks({
      strategy,
      currentTick: this.currentTick,
      tickSpacing: this.tickSpacing,
    })

    const minPrice = tickToPriceString(
      lowerTick,
      this.pool.token0,
      this.pool.token1,
      PRICE_RANGE_TICK_STORAGE_DECIMALS,
    )
    const maxPrice = tickToPriceString(
      upperTick,
      this.pool.token0,
      this.pool.token1,
      PRICE_RANGE_TICK_STORAGE_DECIMALS,
    )

    this.setFullRange(false)
    this.setMinPrice(minPrice)
    this.setMaxPrice(maxPrice)
    this.setSelectedStrategy(strategy)
    this.reconcileDepositMode()
  }

  /**
   * Read the ERC20 allowance granted to NFPM by the connected account for
   * both tokens of the currently-loaded pool. Drives the CTA pre-check so
   * we can show "Approve WETH" vs "Create position" before the user
   * clicks. Called on pool load, on wallet account change, and after a
   * position is successfully created. Safe to call when no wallet or no
   * pool is loaded — it simply no-ops and clears the cached values.
   */
  async refreshAllowances(): Promise<void> {
    const pool = this.pool
    const account = this.walletStore.getConnectedAddress()
    if (!pool || !account) {
      runInAction(() => {
        this.token0AllowanceWei = undefined
        this.token1AllowanceWei = undefined
        this.isLoadingAllowances = false
      })
      return
    }

    runInAction(() => {
      this.isLoadingAllowances = true
    })

    try {
      const token0Address = pool.token0.address.toViemAddress()
      const token1Address = pool.token1.address.toViemAddress()
      const [allowance0, allowance1] = await Promise.all([
        readContract(this.config, {
          address: token0Address,
          abi: erc20Abi,
          functionName: "allowance",
          args: [account, NFP_MANAGER_ADDRESS],
          chainId: this.chainId,
        }),
        readContract(this.config, {
          address: token1Address,
          abi: erc20Abi,
          functionName: "allowance",
          args: [account, NFP_MANAGER_ADDRESS],
          chainId: this.chainId,
        }),
      ])
      runInAction(() => {
        this.token0AllowanceWei = BigInt((allowance0 as bigint).toString())
        this.token1AllowanceWei = BigInt((allowance1 as bigint).toString())
        this.isLoadingAllowances = false
      })
    } catch (error) {
      console.error("[CreatePositionStore.refreshAllowances] failed", error)
      runInAction(() => {
        // Keep the last known values. Clearing both sides after one read
        // failure can erase an approval that already succeeded in this run
        // and send the CTA back to a token the user has just approved.
        this.isLoadingAllowances = false
      })
    }
  }

  /**
   * Reset the stepper UI back to idle. Clears any pending delayed-hide
   * timer (the composite schedules one after a successful mint so the
   * success state is visible for a beat before the card reverts).
   */
  clearStepProgress(): void {
    this.activeStep = undefined
    this.completedSteps = []
    this.stepErrorMessage = undefined
    this.activeStepStartedAt = undefined
    this.pendingStepId = undefined
    this.pendingTxHash = undefined
    if (this.stepClearTimer) {
      clearTimeout(this.stepClearTimer)
      this.stepClearTimer = undefined
    }
  }

  async syncProcessFromChain(): Promise<void> {
    if (!this.pool) return

    runInAction(() => {
      this.isCheckingProcess = true
    })

    try {
      await this.refreshAllowances()

      runInAction(() => {
        const completedBefore = this.completedSteps.length
        const desired0 = this.amount0DesiredWei
        const desired1 = this.amount1DesiredWei

        if (
          desired0 > 0n &&
          this.token0AllowanceWei !== undefined &&
          this.token0AllowanceWei >= desired0 &&
          !this.completedSteps.includes("approve:token0")
        ) {
          this.completedSteps.push("approve:token0")
        }

        if (
          desired1 > 0n &&
          this.token1AllowanceWei !== undefined &&
          this.token1AllowanceWei >= desired1 &&
          !this.completedSteps.includes("approve:token1")
        ) {
          this.completedSteps.push("approve:token1")
        }

        if (this.createdTokenId !== undefined && !this.completedSteps.includes("mint")) {
          this.completedSteps.push("mint")
        }

        const observedProgress = this.completedSteps.length > completedBefore
        if (observedProgress) {
          this.stepErrorMessage = undefined
          if (this.activeStep && this.completedSteps.includes(this.activeStep.id)) {
            this.activeStep = undefined
            this.activeStepStartedAt = undefined
          }
        }
      })
    } finally {
      runInAction(() => {
        this.isCheckingProcess = false
      })
    }
  }

  /**
   * Single entry point for the morphing CTA. When the pre-check has seen
   * a concrete shortfall on a specific token, this fires a scoped approval
   * tx and then re-reads the allowance so the next click can advance. When
   * all allowances are already in place (or when we don't have a cached
   * view — e.g. allowance read failed), we hand off to the full-chain
   * `createPosition` interaction, which itself re-reads allowances and
   * runs approvals+mint in a single click. Either path keeps the stepper
   * UI consistent via the typed emitter events.
   */
  async startNextStepForConnectedWallet(): Promise<void> {
    const account = this.walletStore.getConnectedAddress()
    if (!account) {
      runInAction(() => {
        this.creationError = "Wallet not connected"
      })
      return
    }
    if (!this.pool || !this.canCreatePosition) {
      return
    }

    // Reset the stepper for a fresh run. Keeps the UI honest when the user
    // retries after an error without reloading the page. Clearing
    // `creationError` here lets `canCreatePosition` flip back to `true`
    // after a retry (the getter gates on `creationError === undefined`).
    this.clearStepProgress()
    runInAction(() => {
      this.creationError = undefined
    })

    while (this.requiredApprovals.length > 0) {
      const next = this.requiredApprovals[0]
      if (!next) break
      const approved = await this.approveNextToken(next, account)
      if (!approved) return
    }

    await this.createPosition(account)
  }

  /**
   * Fire a single ERC20.approve tx for the token identified by `which`
   * and refresh the cached allowance on success. Kept private because the
   * public surface is `startNextStepForConnectedWallet` — external callers
   * should never pick which token to approve; that decision belongs to
   * the pre-check pipeline.
   */
  private async approveNextToken(
    required: { which: "token0" | "token1"; desired: bigint },
    account: Address,
  ): Promise<boolean> {
    const pool = this.pool
    if (!pool) return false
    const token = required.which === "token0" ? pool.token0 : pool.token1
    const stepName: CreatePositionStepName =
      required.which === "token0" ? "approve:token0" : "approve:token1"

    this.emitter.emit(stepName, {
      title: `Approve ${token.symbol}`,
      variant: "info",
    })
    runInAction(() => {
      this.isCreating = true
    })

    const result = await approveOwnerSpenderAllowance({
      account,
      chainId: this.chainId,
      config: this.config,
      contractAddress: token.address.toViemAddress(),
      spenderAddress: NFP_MANAGER_ADDRESS,
      spenderAmount: required.desired,
      approvalMode: this.approvalModeProvider.getApprovalMode(),
      contractRiskLevel: "immutable",
      receiptWait: MANUAL_CL_RECEIPT_WAIT_OPTIONS,
    })

    runInAction(() => {
      this.pendingTxHash = result.tx
      this.pendingStepId = stepName
    })

    let approved = result.success
    if (result.success || result.tx) {
      try {
        await waitForAllowance({
          config: this.config,
          chainId: this.chainId,
          contractAddress: token.address.toViemAddress(),
          ownerAddress: account,
          spenderAddress: NFP_MANAGER_ADDRESS,
          requiredAmountWei: required.desired,
        })
        approved = true
      } catch (error) {
        if (!result.success) {
          console.warn("[CreatePositionStore.approveNextToken] receipt timed out and allowance recovery failed", {
            tx: result.tx,
            error,
          })
        }
      }
    }

    if (approved) {
      this.emitter.emit(stepName, {
        title: `${token.symbol} approved`,
        variant: "success",
      })
      await this.refreshAllowances()
      if (!this.approvalCacheCovers(required)) {
        await this.refreshAllowances()
      }
      if (!this.approvalCacheCovers(required)) {
        runInAction(() => {
          if (required.which === "token0") {
            this.token0AllowanceWei = required.desired
          } else {
            this.token1AllowanceWei = required.desired
          }
        })
      }
    } else {
      this.emitter.emit(stepName, {
        title: `Unable to approve ${token.symbol}`,
        description: humanizeTransactionError(result.error) ?? undefined,
        variant: "error",
        errorMessage: result.error,
      })
    }

    runInAction(() => {
      this.isCreating = false
    })
    return approved
  }

  private approvalCacheCovers(required: { which: "token0" | "token1"; desired: bigint }): boolean {
    const cached =
      required.which === "token0"
        ? this.token0AllowanceWei
        : this.token1AllowanceWei
    return cached !== undefined && cached >= required.desired
  }

  async createPosition(account: Address): Promise<void> {
    if (!this.canCreatePosition || !this.pool) {
      return
    }

    this.isCreating = true
    this.creationError = undefined

    try {
      const token0 = this.pool.token0
      const token1 = this.pool.token1

      const calculation = this.priceRangeCalculation
      if (!token0 || !token1 || calculation.ticks[0] === undefined || calculation.ticks[1] === undefined) {
        throw new Error("Missing required position parameters")
      }

      // Parse amounts to bigint (mode-aware safety: unused side is always 0)
      const mode = this.depositMode
      const amount0Desired =
        mode === "single_token1" ? 0n : parseUnits(this.amount0 === "" ? "0" : this.amount0, token0.decimals)
      const amount1Desired =
        mode === "single_token0" ? 0n : parseUnits(this.amount1 === "" ? "0" : this.amount1, token1.decimals)

      if (amount0Desired === 0n && amount1Desired === 0n) {
        throw new Error("At least one amount must be greater than zero")
      }

      const result = await createPositionInteraction({
        account,
        chainId: this.chainId,
        config: this.config,
        token0: {
          address: token0.address.toViemAddress(),
          symbol: token0.symbol,
          decimals: token0.decimals,
        },
        token1: {
          address: token1.address.toViemAddress(),
          symbol: token1.symbol,
          decimals: token1.decimals,
        },
        fee: this.pool.feeTier,
        tickLower: BigInt(calculation.ticks[0]),
        tickUpper: BigInt(calculation.ticks[1]),
        amount0Desired,
        amount1Desired,
        amount0Min: mode === "single_token1" ? 0n : this.amount0Min,
        amount1Min: mode === "single_token0" ? 0n : this.amount1Min,
        deadline: this.getSubmitDeadline(),
        nfpManagerAddress: NFP_MANAGER_ADDRESS,
        poolAddress: this.pool.address.toViemAddress(),
        emitter: this.emitter,
        eventBus: this.eventBus,
        approvalMode: this.approvalModeProvider.getApprovalMode(),
      })

      runInAction(() => {
        this.isCreating = false
        this.pendingTxHash = result.tx
        this.pendingStepId = "mint"
      })

      // On success, leave the stepper visible for a beat so the user sees
      // every checkmark land before the card reverts to idle. On failure,
      // leave the failed state up until the user retries (which calls
      // `clearStepProgress` via `startNextStepForConnectedWallet`).
      if (result.success) {
        const tokenId = (() => {
          const outputs = result.simulatedResult as Array<unknown> | undefined
          const maybeTokenId = Array.isArray(outputs) ? outputs[0] : undefined
          return typeof maybeTokenId === "bigint" ? maybeTokenId : undefined
        })()
        runInAction(() => {
          this.createdTokenId = tokenId
          this.createdPoolAddress = this.pool?.address.toViemAddress()
        })
        if (this.stepClearTimer) clearTimeout(this.stepClearTimer)
        this.stepClearTimer = setTimeout(() => {
          runInAction(() => {
            this.clearStepProgress()
          })
        }, 1500)
      }
    } catch (error) {
      runInAction(() => {
        this.isCreating = false
        this.creationError = error instanceof Error ? error.message : "Failed to create position"
      })
    }
  }

  async createPositionForConnectedWallet(): Promise<void> {
    const account = this.walletStore.getConnectedAddress()
    if (!account) {
      runInAction(() => {
        this.creationError = "Wallet not connected"
      })
      return
    }
    await this.createPosition(account)
  }

  reset(): void {
    this.pool = undefined
    this.minPrice = undefined
    this.maxPrice = undefined
    this.rangeSpreadPctInput = ""
    this.inputMode = "price"
    this.fullRange = false
    this.isPriceRangeInverted = false
    this.selectedStrategy = undefined
    this.amount0 = ""
    this.amount1 = ""
    this.activeDepositInput = undefined
    this.slippage = DEFAULT_SLIPPAGE
    this.deadlineDurationSeconds = DEFAULT_DEADLINE_SECONDS
    this.deadline = nowSeconds() + DEFAULT_DEADLINE_SECONDS
    this.creationError = undefined
    this.token0AllowanceWei = undefined
    this.token1AllowanceWei = undefined
    this.isLoadingAllowances = false
    this.isCheckingProcess = false
    this.createdTokenId = undefined
    this.createdPoolAddress = undefined
    this.clearStepProgress()
  }

  // Computed properties

  get hasPool(): boolean {
    return this.pool !== undefined
  }

  /**
   * Single atomic computed property that calculates all price range values
   * Uses Strategy pattern to delegate to FullRange or CustomRange strategy
   * This eliminates cascading computed properties and prevents reactive loops
   */
  get priceRangeCalculation(): PriceRangeCalculationResult {
    // Validate required inputs
    if (!this.pool || !this.tickSpacing || this.currentTick === undefined) {
      return DEFAULT_PRICE_RANGE_RESULT
    }

    // Create appropriate strategy based on fullRange flag
    const strategy = PriceRangeCalculationStrategyFactory.create(this.fullRange)

    // Calculate all values in a single atomic operation
    return strategy.calculate({
      minPrice: this.minPrice,
      maxPrice: this.maxPrice,
      inputMode: this.inputMode,
      fullRange: this.fullRange,
      currentPrice: this.currentPrice,
      currentTick: this.currentTick,
      tickSpacing: this.tickSpacing,
      token0: this.pool.token0,
      token1: this.pool.token1,
    })
  }

  get depositMode(): DepositMode {
    const pool = this.pool
    const currentTick = this.currentTick
    const [tickLower, tickUpper] = this.priceRangeCalculation.ticks

    if (!pool || currentTick === undefined || tickLower === undefined || tickUpper === undefined) {
      return "unknown"
    }

    if (currentTick < tickLower) {
      return "single_token0"
    }
    if (currentTick > tickUpper) {
      return "single_token1"
    }
    return "dual"
  }

  get isToken0DepositDisabled(): boolean {
    return this.depositMode === "single_token1"
  }

  get isToken1DepositDisabled(): boolean {
    return this.depositMode === "single_token0"
  }

  get hasValidAmounts(): boolean {
    const amount0Num = parseFloat(this.amount0 || "0")
    const amount1Num = parseFloat(this.amount1 || "0")
    return amount0Num > 0 || amount1Num > 0
  }

  get canCreatePosition(): boolean {
    return (
      this.hasPool &&
      this.priceRangeCalculation.hasValidRange &&
      this.hasValidAmounts &&
      !this.isCreating &&
      this.creationError === undefined
    )
  }

  get tickSpacing(): number | undefined {
    if (!this.pool) {
      return undefined
    }

    const feeToTicksMap = getFeeToTicksMapForChain(this.chainId)
    return feeToTicksMap[this.pool.feeTier as keyof typeof feeToTicksMap]
  }

  get currentTick(): number | undefined {
    return this.pool?.currentTick
  }

  /**
   * Prefer on-chain slot0 price; fall back to tick-derived sqrtRatio when
   * slot0 hasn't been fetched yet (first paint, pool just added). The fallback
   * is lossy at sub-tick precision but keeps the UI functional before the
   * pool on-chain transport refreshes.
   */
  get currentSqrtPriceX96(): bigint | undefined {
    if (this.pool?.currentSqrtPriceX96 !== undefined) {
      return this.pool.currentSqrtPriceX96
    }
    if (this.currentTick === undefined) return undefined
    try {
      return getSqrtRatioAtTick(this.currentTick)
    } catch {
      return undefined
    }
  }

  get currentPrice(): number | undefined {
    if (!this.pool || this.currentTick === undefined) {
      return undefined
    }

    try {
      const priceString = tickToPriceString(this.currentTick, this.pool.token0, this.pool.token1)
      return parseFloat(priceString) || undefined
    } catch (error) {
      console.error("[CreatePositionStore.currentPrice] Error calculating price:", error)
      return undefined
    }
  }

  get displayedCurrentPrice(): number | undefined {
    const currentPrice = this.currentPrice
    if (currentPrice === undefined) return undefined
    if (!this.isPriceRangeInverted) return currentPrice
    if (currentPrice <= 0) return undefined
    const inverted = 1 / currentPrice
    return Number.isFinite(inverted) ? inverted : undefined
  }

  get displayedPricesAtTicks(): [string | undefined, string | undefined] {
    const calculation = this.priceRangeCalculation
    if (!this.isPriceRangeInverted) return calculation.pricesAtTicks
    if (!this.pool) return calculation.pricesAtTicks
    const [minTick, maxTick] = calculation.ticks
    return [
      this.formatDisplayPriceAtTick(maxTick),
      this.formatDisplayPriceAtTick(minTick),
    ]
  }

  get displayedPriceDifferences(): {
    min: number | undefined
    max: number | undefined
  } {
    const calculation = this.priceRangeCalculation
    if (!this.isPriceRangeInverted) return calculation.priceDifferences
    const currentPrice = this.displayedCurrentPrice
    const [minPrice, maxPrice] = this.displayedPricesAtTicks
    return {
      min: calculatePriceDifference(parseDisplayPrice(minPrice), currentPrice),
      max: calculatePriceDifference(parseDisplayPrice(maxPrice), currentPrice),
    }
  }

  private formatDisplayPriceAtTick(tick: number | undefined): string | undefined {
    if (!this.pool || tick === undefined) return undefined
    return tickToPriceString(
      tick,
      this.pool.token0,
      this.pool.token1,
      PRICE_RANGE_TICK_STORAGE_DECIMALS,
      this.isPriceRangeInverted,
    )
  }

  get priceStrategyConfigs(): Array<PriceStrategyConfig> {
    return getPriceStrategyConfigs()
  }

  get isWalletConnected(): boolean {
    return this.walletStore.isWalletConnected
  }

  get isRangeInvalid(): boolean {
    return (
      !this.priceRangeCalculation.hasValidRange &&
      this.minPrice !== undefined &&
      this.maxPrice !== undefined &&
      !this.fullRange
    )
  }

  /**
   * `true` when the currently-selected range (or full-range) straddles the
   * pool's current tick, i.e. the position would earn fees immediately. Used
   * by the create-position card to surface an "In range" preview badge so LPs
   * get instant feedback while dragging the brush.
   */
  get isProposedRangeInRange(): boolean {
    if (this.currentTick === undefined) return false
    if (this.fullRange) return true
    const [tickLower, tickUpper] = this.priceRangeCalculation.ticks
    if (tickLower === undefined || tickUpper === undefined) return false
    return this.currentTick >= tickLower && this.currentTick < tickUpper
  }

  /**
   * Proportion of MIN_TICK..MAX_TICK (in ticks) covered by the proposed
   * range. Intentionally expressed as percentage-of-full-range rather than
   * percentage-of-TVL — the latter would require us to sum liquidity across
   * every tick in the range which is an expensive derivation and drifts
   * under every swap. This lightweight figure gives LPs a useful "how
   * concentrated am I?" intuition; when the pool's tick distribution is
   * loaded the range-brush overlay shows the precise profile.
   */
  get liquidityCoveragePct(): number {
    if (this.fullRange) return 100
    const [tickLower, tickUpper] = this.priceRangeCalculation.ticks
    if (tickLower === undefined || tickUpper === undefined) return 0
    const width = tickUpper - tickLower
    if (width <= 0) return 0
    const full = MAX_TICK - MIN_TICK
    const pct = (width / full) * 100
    if (!Number.isFinite(pct)) return 0
    return Math.min(100, Math.max(0, pct))
  }

  /**
   * USD value of the current `amount0` deposit input. Uses the pool's
   * token0 `bestPrice` (which is itself derived from `Bundle.ethPriceUSD *
   * Token.derivedETH`) — i.e. the indexer's view of the USD price, not a
   * fabricated number.
   */
  get amount0UsdValue(): number {
    if (!this.pool) return 0
    const amount = parseFloat(this.amount0 || "0")
    if (!Number.isFinite(amount) || amount <= 0) return 0
    const usd = amount * this.pool.token0.bestPrice
    return Number.isFinite(usd) ? usd : 0
  }

  get amount1UsdValue(): number {
    if (!this.pool) return 0
    const amount = parseFloat(this.amount1 || "0")
    if (!Number.isFinite(amount) || amount <= 0) return 0
    const usd = amount * this.pool.token1.bestPrice
    return Number.isFinite(usd) ? usd : 0
  }

  get totalDepositUsdValue(): number {
    return this.amount0UsdValue + this.amount1UsdValue
  }

  get amount0Min(): bigint {
    if (!this.pool || !this.amount0) {
      return 0n
    }

    const amount0Desired = parseUnits(this.amount0, this.pool.token0.decimals)
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (amount0Desired * slippageMultiplier) / 10000n
  }

  get amount1Min(): bigint {
    if (!this.pool || !this.amount1) {
      return 0n
    }

    const amount1Desired = parseUnits(this.amount1, this.pool.token1.decimals)
    const slippageMultiplier = BigInt(Math.floor((100 - this.slippage) * 100))
    return (amount1Desired * slippageMultiplier) / 10000n
  }

  /**
   * Range-aware APR projection for the position the user is about to
   * create. Reacts to every range-brush drag, deposit edit, and pool-state
   * refresh: changes in `priceRangeCalculation.ticks`,
   * `totalDepositUsdValue`, `pool.currentTick`,
   * `pool.currentSqrtPriceX96`, `pool.volumeUsd24h`, `pool.liquidity`,
   * and the tick distribution's `activeLiquidityAt(currentTick)` all
   * re-compute this value through standard MobX tracking.
   *
   * Two regimes exist. Once the user has entered deposit amounts we hand
   * off to the pure `concentratedApr` function, which models exact
   * fee-share dilution against combined pool + prospective liquidity. If
   * the user has not entered amounts yet (first paint, strategy just
   * applied), we fall back to a size-independent baseline using the
   * identity
   *
   *   aprPct ≈ poolBaselineAPR × capitalEfficiency
   *
   * which is exact in the zero-deposit limit (a position small enough
   * not to dilute the pool earns `capitalEfficiency` times the pool's
   * headline APR). Without this fallback the UI shows `0.00%` for every
   * range until the first keystroke, which is misleading — users can't
   * compare ranges before committing capital.
   *
   * Returns a safe zero-tuple when the pool state itself is missing so
   * the view layer can render unconditionally without null-guarding.
   */
  get estimatedApr(): {
    aprPct: number
    dailyFeesUsd: number
    capitalEfficiency: number
    inRange: boolean
    formattedPct: string
    formattedDailyUsd: string
  } {
    const zero = {
      aprPct: 0,
      dailyFeesUsd: 0,
      capitalEfficiency: 0,
      inRange: false,
      formattedPct: "0.00%",
      formattedDailyUsd: "",
    }

    const pool = this.pool
    const currentTick = this.currentTick
    const sqrtPriceX96Current = this.currentSqrtPriceX96
    const [tickLower, tickUpper] = this.priceRangeCalculation.ticks

    if (
      !pool ||
      currentTick === undefined ||
      sqrtPriceX96Current === undefined ||
      tickLower === undefined ||
      tickUpper === undefined
    ) {
      return zero
    }

    const positionValueUsd = this.totalDepositUsdValue
    const positionLiquidity = this.computeProspectiveLiquidity({
      pool,
      sqrtPriceX96Current,
      tickLower,
      tickUpper,
    })

    // Pool-wide active liquidity denominator: prefer the tick-distribution
    // store when it is scoped to this pool; otherwise fall back to
    // `pool.liquidity` (the indexer's native `Pool.liquidity` field, which
    // equals the on-chain `pool.liquidity()` slot at the current tick).
    const totalActiveLiquidity = this.resolveTotalActiveLiquidity(pool, currentTick)

    const hasDeposit = positionLiquidity > 0n && positionValueUsd > 0

    if (hasDeposit) {
      const result = concentratedApr({
        currentTick,
        feeTierBps: pool.feeTier,
        volumeUsd24h: pool.volumeUsd24h,
        totalActiveLiquidity,
        tickLower,
        tickUpper,
        positionLiquidity,
        positionValueUsd,
        positionAlreadyInPool: false,
      })

      return {
        aprPct: result.aprPct,
        dailyFeesUsd: result.dailyFeesUsd,
        capitalEfficiency: result.capitalEfficiency,
        inRange: result.inRange,
        formattedPct: formatAprPct(result.aprPct),
        formattedDailyUsd: formatDailyUsd(result.dailyFeesUsd),
      }
    }

    const baseline = computeSizeIndependentApr({
      pool,
      currentTick,
      tickLower,
      tickUpper,
    })

    return {
      aprPct: baseline.aprPct,
      dailyFeesUsd: 0,
      capitalEfficiency: baseline.capitalEfficiency,
      inRange: baseline.inRange,
      formattedPct: formatAprPct(baseline.aprPct),
      formattedDailyUsd: "",
    }
  }

  /**
   * Choose the best available denominator for in-range virtual
   * liquidity. Same preference order as the per-position store —
   * distribution-book first (scoped to this pool), then the indexer's
   * `Pool.liquidity` mirror as a universally-available fallback.
   */
  private resolveTotalActiveLiquidity(
    pool: ManualClPoolStore,
    currentTick: number,
  ): bigint {
    const store = this.tickDistributionStore
    if (store) {
      // `address.raw` is normalised upstream, but guard defensively — tick
      // distribution is the single biggest APR input and we never want a
      // malformed fixture or missing field to throw inside a MobX computed.
      const positionPool = pool.address?.raw?.toLowerCase?.()
      const activePool = store.activePoolAddress?.toLowerCase?.()
      if (positionPool && activePool && positionPool === activePool) {
        const fromDistribution = store.activeLiquidityAt(currentTick)
        if (fromDistribution > 0n) return fromDistribution
      } else if (!positionPool) {
        // Fixture without a canonical address: fall back to using whatever
        // the distribution store is currently serving so existing unit
        // tests keep deriving liquidity from the mocked tick book.
        const fromDistribution = store.activeLiquidityAt(currentTick)
        if (fromDistribution > 0n) return fromDistribution
      }
    }
    if (pool.liquidity > 0n) return pool.liquidity
    return 0n
  }

  /**
   * Convert the user's current deposit + range into a virtual liquidity
   * `L` via the v3-SDK wrapper. Picks the token side that carries a
   * meaningful amount so we stay accurate in both single-sided
   * (out-of-range) and dual-sided configurations.
   */
  private computeProspectiveLiquidity(args: {
    pool: ManualClPoolStore
    sqrtPriceX96Current: bigint
    tickLower: number
    tickUpper: number
  }): bigint {
    const { pool, sqrtPriceX96Current, tickLower, tickUpper } = args
    const amount0Raw = parseOrZero(this.amount0, pool.token0.decimals)
    const amount1Raw = parseOrZero(this.amount1, pool.token1.decimals)

    if (amount0Raw <= 0n && amount1Raw <= 0n) return 0n

    try {
      if (amount0Raw > 0n) {
        return positionFromAmount0({
          sqrtPriceX96: sqrtPriceX96Current,
          tickLower,
          tickUpper,
          amount0: amount0Raw,
          token0: pool.token0,
          token1: pool.token1,
          fee: pool.feeTier,
        }).liquidity
      }
      return positionFromAmount1({
        sqrtPriceX96: sqrtPriceX96Current,
        tickLower,
        tickUpper,
        amount1: amount1Raw,
        token0: pool.token0,
        token1: pool.token1,
        fee: pool.feeTier,
      }).liquidity
    } catch {
      return 0n
    }
  }

  /**
   * Wei-denominated desired amounts used by the approval pre-check. Mode-
   * aware: in a single-sided deposit the unused side is always `0n`, which
   * matches what we'd pass to `mint()` so the allowance check doesn't
   * demand approval for a token the user isn't actually depositing.
   */
  get amount0DesiredWei(): bigint {
    if (!this.pool) return 0n
    if (this.depositMode === "single_token1") return 0n
    return parseOrZero(this.amount0, this.pool.token0.decimals)
  }

  get amount1DesiredWei(): bigint {
    if (!this.pool) return 0n
    if (this.depositMode === "single_token0") return 0n
    return parseOrZero(this.amount1, this.pool.token1.decimals)
  }

  /**
   * Tokens that still need an approval tx before we can call `mint()`.
   * Ordered `token0` before `token1` so the stepper UI walks through
   * approvals in a stable sequence. Returns `[]` as soon as the cached
   * allowances cover the desired amounts.
   *
   * Treat an unknown cache as a shortfall so the scoped approval runner
   * cannot strand the stepper with a pending token after the first approval.
   */
  get requiredApprovals(): Array<{
    which: "token0" | "token1"
    symbol: string
    desired: bigint
  }> {
    const pool = this.pool
    if (!pool) return []
    const list: Array<{ which: "token0" | "token1"; symbol: string; desired: bigint }> = []
    const desired0 = this.amount0DesiredWei
    const desired1 = this.amount1DesiredWei
    const cached0 = this.token0AllowanceWei
    const cached1 = this.token1AllowanceWei
    if (desired0 > 0n && (cached0 === undefined || cached0 < desired0)) {
      list.push({ which: "token0", symbol: pool.token0.symbol, desired: desired0 })
    }
    if (desired1 > 0n && (cached1 === undefined || cached1 < desired1)) {
      list.push({ which: "token1", symbol: pool.token1.symbol, desired: desired1 })
    }
    return list
  }

  get approvalStatus(): "loading" | "needs-approval" | "ready" {
    if (this.isLoadingAllowances) return "loading"
    if (this.requiredApprovals.length > 0) return "needs-approval"
    return "ready"
  }

  /**
   * Drives the CTA label and the click handler. See `CreatePositionCtaState`
   * for the semantics of each variant. Intentionally does not consult
   * `approvalStatus === "loading"` when the allowance cache is already
   * warm (pool reload): we don't want the button to flicker to
   * "Loading…" on every amount edit.
   */
  get ctaState(): CreatePositionCtaState {
    if (!this.isWalletConnected) return "connect"
    if (this.isCreating) return "in-flight"
    if (!this.hasValidAmounts || !this.priceRangeCalculation.hasValidRange) {
      return "enter-amount"
    }
    if (this.isLoadingAllowances && this.token0AllowanceWei === undefined) {
      return "loading-approvals"
    }
    const approvals = this.requiredApprovals
    if (approvals[0]?.which === "token0") return "approve-token0"
    if (approvals[0]?.which === "token1") return "approve-token1"
    return "create-position"
  }

  /**
   * Step list consumed by `CreatePositionProcessView`. Only includes
   * steps that are part of *this* run: if a user already has allowance
   * for token0, we skip the approve-token0 step instead of rendering a
   * phantom checkmark. The composite picks the first step in the list
   * whose status is not "done" to drive animations.
   */
  get processSteps(): Array<{
    id: CreatePositionStepName
    title: string
    status: "pending" | "active" | "done" | "failed"
  }> {
    const pool = this.pool
    if (!pool) return []

    const desired0 = this.amount0DesiredWei
    const desired1 = this.amount1DesiredWei
    const allowance0 = this.token0AllowanceWei
    const allowance1 = this.token1AllowanceWei

    const needs0 =
      desired0 > 0n && (allowance0 === undefined || allowance0 < desired0)
    const needs1 =
      desired1 > 0n && (allowance1 === undefined || allowance1 < desired1)

    const steps: Array<{
      id: CreatePositionStepName
      title: string
      status: "pending" | "active" | "done" | "failed"
    }> = []

    // Always include a step once it has been observed to fire, even when
    // the cached allowance later flips to "sufficient" (post-approval);
    // `completedSteps` is our source of truth for "did this step run?".
    if (needs0 || this.completedSteps.includes("approve:token0") || this.activeStep?.id === "approve:token0") {
      steps.push({
        id: "approve:token0",
        title: `Approve ${pool.token0.symbol}`,
        status: this.resolveStepStatus("approve:token0"),
      })
    }
    if (needs1 || this.completedSteps.includes("approve:token1") || this.activeStep?.id === "approve:token1") {
      steps.push({
        id: "approve:token1",
        title: `Approve ${pool.token1.symbol}`,
        status: this.resolveStepStatus("approve:token1"),
      })
    }
    steps.push({
      id: "mint",
      title: "Create position",
      status: this.resolveStepStatus("mint"),
    })

    return steps
  }

  private resolveStepStatus(
    id: CreatePositionStepName,
  ): "pending" | "active" | "done" | "failed" {
    if (this.activeStep?.id === id) return this.activeStep.status
    if (this.completedSteps.includes(id)) return "done"
    return "pending"
  }
}

/**
 * Size-independent APR baseline used in the create-position flow before
 * the user has entered any deposit amount. Uses the closed-form identity
 *
 *   aprPct = poolBaselineAPR × capitalEfficiency
 *
 * which is exact in the zero-deposit limit. The result falls through to
 * `0` when the range is out of bounds (`inRange === false`) — the
 * concentrated-liquidity capital-efficiency multiplier is defined for
 * every regime, but a prospective position sitting outside the current
 * tick earns nothing until price re-enters, and reporting any positive
 * APR there would be misleading.
 */
function computeSizeIndependentApr(args: {
  pool: ManualClPoolStore
  currentTick: number
  tickLower: number
  tickUpper: number
}): {
  aprPct: number
  capitalEfficiency: number
  inRange: boolean
} {
  const { pool, currentTick, tickLower, tickUpper } = args

  // Reuse the same capital-efficiency math as `concentratedApr` by
  // feeding it placeholder liquidity / value inputs and pulling the
  // `capitalEfficiency` field out of the result. The CE calculation is
  // independent of liquidity so this is a valid probe.
  const probe = concentratedApr({
    currentTick,
    feeTierBps: pool.feeTier,
    volumeUsd24h: 0,
    totalActiveLiquidity: 0n,
    tickLower,
    tickUpper,
    positionLiquidity: 0n,
    positionValueUsd: 0,
    positionAlreadyInPool: false,
  })
  const capitalEfficiency = probe.capitalEfficiency
  const inRange = tickLower <= currentTick && currentTick < tickUpper

  if (!inRange || !(capitalEfficiency > 0)) {
    return { aprPct: 0, capitalEfficiency, inRange }
  }

  const tvl = pool.tvlUsd
  const volume = pool.volumeUsd24h
  const feeRate = pool.feeTier / 1_000_000
  if (!tvl || tvl <= 0 || !Number.isFinite(tvl)) {
    return { aprPct: 0, capitalEfficiency, inRange }
  }
  if (!volume || volume <= 0 || !Number.isFinite(volume)) {
    return { aprPct: 0, capitalEfficiency, inRange }
  }

  const poolBaselineAprPct = ((volume * feeRate * 365) / tvl) * 100
  if (!Number.isFinite(poolBaselineAprPct) || poolBaselineAprPct <= 0) {
    return { aprPct: 0, capitalEfficiency, inRange }
  }

  const aprPct = poolBaselineAprPct * capitalEfficiency
  return {
    aprPct: Number.isFinite(aprPct) ? aprPct : 0,
    capitalEfficiency,
    inRange,
  }
}

function parseOrZero(value: string | undefined, decimals: number | undefined): bigint {
  if (!value || decimals === undefined) return 0n
  const trimmed = value.trim()
  if (trimmed === "" || trimmed === "0" || trimmed === "0." || trimmed === ".") return 0n
  try {
    return parseUnits(trimmed, decimals)
  } catch {
    return 0n
  }
}

function parseDisplayPrice(value: string | undefined): number | undefined {
  if (!value || value === "∞") return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

function nowSeconds(): number {
  return Math.floor(Date.now() / 1000)
}

function formatAprPct(aprPct: number): string {
  if (!Number.isFinite(aprPct) || aprPct <= 0) return "0.00%"
  if (aprPct < 0.01) return "<0.01%"
  return `${aprPct.toFixed(2)}%`
}

function formatDailyUsd(dailyUsd: number): string {
  // Zero / unknown → empty string so the hint line collapses to just the
  // capital-efficiency text instead of showing a meaningless "$0.00/day".
  // The size-independent baseline APR path (no deposit entered yet) and
  // the out-of-range path both legitimately have no per-day projection.
  if (!Number.isFinite(dailyUsd) || dailyUsd <= 0) return ""
  if (dailyUsd < 0.01) return "< $0.01/day"
  if (dailyUsd < 1) return `$${dailyUsd.toFixed(2)}/day`
  if (dailyUsd < 1000) return `$${dailyUsd.toFixed(2)}/day`
  return `$${Math.round(dailyUsd).toLocaleString()}/day`
}
